import { isPostHogBrowserCaptureEnabled } from "./posthog-environment";
import { POSTHOG_DISTINCT_ID_FORM_FIELD, POSTHOG_EVENT_CONTEXT_FORM_FIELD } from "./posthog-constants";

export type PostHogEventContext = {
	distinctId?: string;
	sessionId?: string;
	currentUrl?: string;
	referrer?: string;
	userAgent?: string;
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmTerm?: string;
	utmContent?: string;
	utmId?: string;
	disabled?: boolean;
};

export const POSTHOG_UTM_CONTEXT_FIELDS = {
	utmSource: "utm_source",
	utmMedium: "utm_medium",
	utmCampaign: "utm_campaign",
	utmTerm: "utm_term",
	utmContent: "utm_content",
	utmId: "utm_id",
} as const;

function boundedString(value: unknown, limit: number): string | undefined {
	if (typeof value !== "string") return undefined;
	return (
		value
			.replace(/[\u0000-\u001f\u007f]/g, "")
			.trim()
			.slice(0, limit) || undefined
	);
}

/** Public navigation context only; checkout IDs, email queries and capabilities are discarded. */
function publicUrl(value: unknown): string | undefined {
	if (typeof value !== "string" || value.length > 2048) return undefined;
	try {
		const url = new URL(value);
		if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
		url.username = "";
		url.password = "";
		url.hash = "";
		const query = new URLSearchParams();
		for (const key of Object.values(POSTHOG_UTM_CONTEXT_FIELDS)) {
			const campaign = boundedString(url.searchParams.get(key), 200);
			if (campaign) query.set(key, campaign);
		}
		url.search = query.toString();
		return url.toString();
	} catch {
		return undefined;
	}
}

/** Shared by browser handoff, form fields and durable order metadata. */
export function sanitizePostHogEventContext(input: unknown): PostHogEventContext {
	if (typeof input !== "object" || input === null || Array.isArray(input)) return {};
	const source = input as Record<string, unknown>;
	if (source.disabled === true) return { disabled: true };
	const context: PostHogEventContext = {};
	if (source.disabled === false) context.disabled = false;
	for (const key of ["distinctId", "sessionId"] as const) {
		const value = boundedString(source[key], 200);
		if (value) context[key] = value;
	}
	for (const key of ["currentUrl", "referrer"] as const) {
		const value = publicUrl(source[key]);
		if (value) context[key] = value;
	}
	const userAgent = boundedString(source.userAgent, 1000);
	if (userAgent) context.userAgent = userAgent;
	for (const key of Object.keys(POSTHOG_UTM_CONTEXT_FIELDS) as Array<
		keyof typeof POSTHOG_UTM_CONTEXT_FIELDS
	>) {
		const value = boundedString(source[key], 200);
		if (value) context[key] = value;
	}
	return context;
}

/** Provide the actual browser agent so PostHog can classify bots itself. */
export function postHogContextProperties(input?: PostHogEventContext): Record<string, unknown> {
	const context = sanitizePostHogEventContext(input);
	if (context.disabled) return {};
	const properties: Record<string, unknown> = {};
	if (context.sessionId) properties.$session_id = context.sessionId;
	if (context.currentUrl) {
		properties.$current_url = context.currentUrl;
		properties.$host = new URL(context.currentUrl).host;
	}
	if (context.referrer) properties.$referrer = context.referrer;
	if (context.userAgent) properties.$raw_user_agent = context.userAgent;
	for (const [key, property] of Object.entries(POSTHOG_UTM_CONTEXT_FIELDS)) {
		const value = context[key as keyof typeof POSTHOG_UTM_CONTEXT_FIELDS];
		if (value) {
			properties[property] = value;
			properties[`$initial_${property}`] = value;
		}
	}
	return properties;
}

/** Explicit disabled wins over the compatibility distinct-ID field. */
export function postHogEventContextFromFormData(formData: FormData): PostHogEventContext {
	const raw = formData.get(POSTHOG_EVENT_CONTEXT_FORM_FIELD);
	let context: PostHogEventContext = {};
	if (typeof raw === "string" && raw.length <= 12000) {
		try {
			context = sanitizePostHogEventContext(JSON.parse(raw));
		} catch {
			/* Legacy callers remain supported. */
		}
	}
	if (context.disabled || context.distinctId) return context;
	return sanitizePostHogEventContext({
		...context,
		distinctId: formData.get(POSTHOG_DISTINCT_ID_FORM_FIELD),
	});
}

/** Legacy contexts without a URL remain supported; explicit preview/local context does not. */
export function isPostHogEventContextCaptureEnabled(input?: PostHogEventContext): boolean {
	const context = sanitizePostHogEventContext(input);
	if (context.disabled) return false;
	if (!context.currentUrl) return true;
	return isPostHogBrowserCaptureEnabled({
		nodeEnv: "production",
		disablePostHog: false,
		hostname: new URL(context.currentUrl).hostname,
	});
}
